import { Button, MenuItem, Select, type DialogProps } from '@mui/material';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useCreateEvent } from '~/api/events/create';
import { Dialog, DialogContentTitle, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib';
import { useEventFormik } from '../lib/event-formik';
import { EventForm, type EventMutationValues } from './event-form';

interface EventTypeOption {
  readonly id: number;
  readonly name: string;
}

export interface NewEventDialogProps extends Omit<DialogProps, 'children'> {
  close: () => void;
  /** With more than one, the user picks which the event is for. */
  eventTypes: readonly EventTypeOption[];
  /** Offered as the start when set, e.g. from a calendar click; otherwise now. */
  initialStart?: DateTime;
  open: boolean;
}

const eventDefaults = (start = DateTime.now().startOf('minute')) => {
  return {
    includeInPlayoutReporting: false,
    name: '',
    sponsorStartTime: '',
    sponsorStopTime: '',
    start,
    stop: start.plus({ hours: 1 }),
  };
};

export const NewEventDialog = ({ close, eventTypes, initialStart, open }: NewEventDialogProps) => {
  const { t } = useTranslation();
  const [createEvent] = useCreateEvent();

  const [searchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';

  const onlyEventType = eventTypes.length === 1 ? eventTypes[0] : undefined;
  const [pickedEventTypeId, setPickedEventTypeId] = useState<number | ''>('');
  const eventTypeId = onlyEventType?.id ?? pickedEventTypeId;

  const onSubmit = async (values: EventMutationValues) => {
    if (eventTypeId === '') return;
    const { errors } = await createEvent({
      variables: {
        input: {
          ...values,
          eventTypeId,
        },
        search,
      },
    });
    if (!errors?.length) close();
  };

  const formik = useEventFormik(useMemo(eventDefaults, []), onSubmit);

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={close}
      TransitionProps={{
        // Taken on open, so the default start is the clicked slot or the current minute, not page load.
        onEnter: () => formik.resetForm({ values: eventDefaults(initialStart) }),
        onExited: () => {
          formik.resetForm();
          setPickedEventTypeId('');
        },
      }}
    >
      <DialogTitle onClose={close}>
        {t('pages.event_types.events_tab.new_event_dialog.title')}
      </DialogTitle>

      <DialogContent sx={{ overflowX: 'clip' }}>
        {onlyEventType && (
          <DialogContentTitle>
            <Trans
              components={{ strong: <strong /> }}
              i18nKey="pages.event_types.events_tab.new_event_dialog.subtitle"
              values={{ eventTypeName: onlyEventType.name }}
            />
          </DialogContentTitle>
        )}
        <EventForm formik={formik} id="new-event-form">
          {!onlyEventType && (
            <label>
              <span className="label required" id="new-event-type-label">
                {t('common.record_types.event_type')}
              </span>
              <Select
                displayEmpty
                fullWidth
                labelId="new-event-type-label"
                onChange={(event) => setPickedEventTypeId(Number(event.target.value))}
                value={pickedEventTypeId}
              >
                <MenuItem disabled value="">
                  {t('pages.event_types.events_tab.new_event_dialog.select_event_type')}
                </MenuItem>
                {eventTypes.map((eventType) => (
                  <MenuItem key={eventType.id} value={eventType.id}>
                    {eventType.name}
                  </MenuItem>
                ))}
              </Select>
            </label>
          )}
        </EventForm>
      </DialogContent>

      <DialogActions>
        <Button disabled={formik.isSubmitting} onClick={close} variant="outlined">
          {t('common.dialogs.cancel')}
        </Button>

        <Button
          color="primary"
          disabled={formik.isSubmitting || !formik.isValid || !formik.dirty || eventTypeId === ''}
          form="new-event-form"
          type="submit"
          variant="contained"
        >
          {t('common.dialogs.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
